import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const TimeBarContext = createContext(null);

// sessionStorage key — survives navigation / WS reconnects within the same
// browser tab so a given alert toasts at most ONCE per session. Closing the
// tab resets it, which is the desired UX (user gets a single nudge per session,
// then everything stays in the dropdown).
const SEEN_KEYS_STORAGE = 'panorix.timebar.seenAlertKeys.v1';

const readSeenKeys = () => {
  try {
    const raw = sessionStorage.getItem(SEEN_KEYS_STORAGE);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const persistSeenKeys = (set) => {
  try {
    sessionStorage.setItem(SEEN_KEYS_STORAGE, JSON.stringify(Array.from(set)));
  } catch { /* quota / disabled — fall through silently */ }
};

export function TimeBarProvider({ children }) {
  const [alerts, setAlerts] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  // Hydrate from sessionStorage so a refresh inside the same tab doesn't
  // re-toast every backlog alert that arrives via the WS initial snapshot.
  const seenAlertKeys = useRef(readSeenKeys());
  // Suppress toasts during the first 4s after mount — the WS server replays
  // the existing alert backlog the moment the socket opens; we don't want
  // those replays to flash on screen on every navigation.
  const toastGateUntilRef = useRef(Date.now() + 4000);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const markSeen = (key) => {
    seenAlertKeys.current.add(key);
    persistSeenKeys(seenAlertKeys.current);
  };

  // Fetch stored alerts via REST
  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('ciq_token');
      if (!token) return;
      const res = await fetch(`${backendUrl}/api/clm/alerts?acknowledged=false&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data);
        // Mark every alert returned by the REST snapshot as already-seen so
        // the WS replay (which races with this fetch) cannot trigger toasts
        // for things the user has already been notified about.
        data.forEach(a => markSeen(`${a.reference}::${a.type}`));
      }
    } catch {}
  }, [backendUrl]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const token = localStorage.getItem('ciq_token');
    if (!token) return;   // the socket is authenticated; no token, no connect
    const wsUrl = backendUrl.replace(/^http/, 'ws')
      + `/api/clm/timebar/ws?token=${encodeURIComponent(token)}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const alert = JSON.parse(event.data);
          const alertKey = `${alert.reference}::${alert.type}`;
          const isNew = !seenAlertKeys.current.has(alertKey);
          markSeen(alertKey);

          setAlerts(prev => {
            const exists = prev.find(a => a.reference === alert.reference && a.type === alert.type);
            if (exists) {
              return prev.map(a => (a.reference === alert.reference && a.type === alert.type) ? { ...a, ...alert } : a);
            }
            return [alert, ...prev];
          });

          // Show toast only for genuinely new alerts (CRITICAL/HIGH), AND only
          // after the initial-load grace window so backlog replay never toasts.
          // After that single nudge, the alert lives in the bell dropdown only.
          const withinGrace = Date.now() < toastGateUntilRef.current;
          if (isNew && !withinGrace && (alert.severity === 'CRITICAL' || alert.severity === 'HIGH')) {
            const isCritical = alert.severity === 'CRITICAL';
            toast[isCritical ? 'error' : 'warning'](alert.message || alert.subject, {
              duration: isCritical ? 10000 : 6000,
              description: alert.time_bar_clause ? `Clause: ${alert.time_bar_clause}` : undefined
            });
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [backendUrl]);

  // Acknowledge an alert
  const acknowledgeAlert = useCallback(async (alertId) => {
    try {
      // via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: 'acknowledge', alert_id: alertId }));
      }
      // also via REST
      const token = localStorage.getItem('ciq_token');
      await fetch(`${backendUrl}/api/clm/alerts/${alertId}/acknowledge`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch {}
  }, [backendUrl]);

  // Request browser notification permission
  const requestPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('ciq_token');
    if (!token) return;
    fetchAlerts();
    connect();
    requestPermission();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [fetchAlerts, connect, requestPermission]);

  const unreadCount = alerts.filter(a => !a.acknowledged).length;
  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL').length;

  return (
    <TimeBarContext.Provider value={{ alerts, unreadCount, criticalCount, connected, acknowledgeAlert, fetchAlerts }}>
      {children}
    </TimeBarContext.Provider>
  );
}

export const useTimeBar = () => useContext(TimeBarContext);
